import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { Home, CandlestickChart, Target, GitMerge } from 'lucide-react';

import { useAuth } from './contexts/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import Footer from './components/Footer';
import NoticeBannerStack from './components/NoticeBannerStack';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import ScrollToTop from './components/ScrollToTop';
import DottedGlowBackground from './components/DottedGlowBackground';

// Legacy /quarterly-report/:quarter URLs (pre-rename to /reports/quarterly/:quarter) still redirect.
function LegacyQuarterlyReportRedirect() {
  const { quarter } = useParams();
  return <Navigate to={`/reports/quarterly/${quarter}`} replace />;
}

// Lazy-loaded pages — only downloaded when navigated to
// Landing + Auth are lazy so Firebase SDK stays out of the initial bundle
const Landing              = lazy(() => import('./pages/Landing').then(m => ({ default: m.Landing })));
const Auth                 = lazy(() => import('./pages/Auth'));
const Dashboard            = lazy(() => import('./pages/Dashboard'));
const PotentialPatternsPage= lazy(() => import('./pages/PotentialPatternsPage').then(m => ({ default: m.PotentialPatternsPage })));
const ConvergencePage      = lazy(() => import('./pages/ConvergencePage').then(m => ({ default: m.ConvergencePage })));
const PatternTrackerPage   = lazy(() => import('./pages/PatternTrackerPage'));
const PerformancePage      = lazy(() => import('./pages/PerformancePage'));

const Profile              = lazy(() => import('./pages/Profile'));
const History              = lazy(() => import('./pages/History'));
const Pricing              = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })));
const Checkout             = lazy(() => import('./pages/Checkout'));
const SuccessPage          = lazy(() => import('./pages/SuccessPage'));
const ResetPassword        = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail          = lazy(() => import('./pages/VerifyEmail'));
const QuarterlyReportPage  = lazy(() => import('./pages/QuarterlyReportPage'));
const QuarterlyReportsIndexPage = lazy(() => import('./pages/QuarterlyReportsIndexPage'));
const PatternReportPage    = lazy(() => import('./pages/PatternReportPage'));
// Learn pages
const LearnIndexPage  = lazy(() => import('./pages/learn/LearnIndexPage'));
const SMCGuide        = lazy(() => import('./pages/learn/SMCGuide'));
const ConvergenceGuide= lazy(() => import('./pages/learn/ConvergenceGuide'));
const PatternsGuide   = lazy(() => import('./pages/learn/PatternsGuide'));
const MCTVLGuide      = lazy(() => import('./pages/learn/MCTVLGuide'));
const PSRatioGuide    = lazy(() => import('./pages/learn/PSRatioGuide'));
const HeadAndShouldersGuide = lazy(() => import('./pages/learn/patterns/HeadAndShouldersGuide'));
const DoubleTopBottomGuide  = lazy(() => import('./pages/learn/patterns/DoubleTopBottomGuide'));
const EngulfingGuide        = lazy(() => import('./pages/learn/patterns/EngulfingGuide'));
const CupAndHandleGuide     = lazy(() => import('./pages/learn/patterns/CupAndHandleGuide'));
const FairValueGapGuide     = lazy(() => import('./pages/learn/patterns/FairValueGapGuide'));
const OrderBlockGuide       = lazy(() => import('./pages/learn/patterns/OrderBlockGuide'));
const AscendingTriangleGuide   = lazy(() => import('./pages/learn/patterns/AscendingTriangleGuide'));
const DescendingTriangleGuide  = lazy(() => import('./pages/learn/patterns/DescendingTriangleGuide'));
const SymmetricalTriangleGuide = lazy(() => import('./pages/learn/patterns/SymmetricalTriangleGuide'));
// About pages
const AboutPage     = lazy(() => import('./pages/about/AboutPage'));
const ContactPage   = lazy(() => import('./pages/about/ContactPage'));
const HelpCenterPage= lazy(() => import('./pages/about/HelpCenterPage'));
// Legal pages
const PrivacyPage   = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage     = lazy(() => import('./pages/legal/TermsPage'));

const NotFoundPage  = lazy(() => import('./pages/NotFoundPage'));
// import PatternAnalysis from './pages/PatternAnalysis.tsx';
// import FirebaseTest from './components/FirebaseTest';

function App() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isLanding = location.pathname === '/'
    || location.pathname === '/track-record'
    || location.pathname === '/reports'
    || location.pathname.startsWith('/reports/quarterly/')
    || location.pathname.startsWith('/reports/pattern/');

  function lastCompletedQuarter(): string {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    const currentQ = Math.ceil((month + 1) / 3);
    if (currentQ === 1) return `${year - 1}-Q4`;
    return `${year}-Q${currentQ - 1}`;
  }



  // CloseProfile
  useEffect(() => {
    const handleClickOutside = () => {
      if (isProfileMenuOpen) setIsProfileMenuOpen(false);
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isProfileMenuOpen, isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Scroll past top → light condense only (tighter padding / denser glass).
  // Labels always stay visible — primary nav should not hide information.
  const [isNavCondensed, setIsNavCondensed] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsNavCondensed(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Premium nav effects: liquid pill + glass spotlight ---
  const navLinksRef = useRef<HTMLDivElement>(null);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [liquid, setLiquid] = useState({ left: 0, width: 0, height: 0, top: 0, ready: false });
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50, on: false });

  // Liquid pill follows hover only; active route uses its own keycap style
  // so click press can scale the button without fighting a shared underlay.
  const measureLiquid = useCallback(() => {
    const root = navLinksRef.current;
    if (!root || !hoveredNav) {
      setLiquid((s) => ({ ...s, ready: false }));
      return;
    }
    const el = root.querySelector<HTMLElement>(`[data-nav-to="${hoveredNav}"]`);
    if (!el) {
      setLiquid((s) => ({ ...s, ready: false }));
      return;
    }
    setLiquid({
      left: el.offsetLeft,
      top: el.offsetTop,
      width: el.offsetWidth,
      height: el.offsetHeight,
      ready: true,
    });
  }, [hoveredNav]);

  useLayoutEffect(() => {
    measureLiquid();
  }, [measureLiquid, isNavCondensed]);

  useEffect(() => {
    window.addEventListener('resize', measureLiquid);
    return () => window.removeEventListener('resize', measureLiquid);
  }, [measureLiquid]);

  const onNavSpotlightMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setSpotlight({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
      on: true,
    });
  };

  const NavLink = ({ to, children, icon, exact = false }: { to: string; children: React.ReactNode; icon: React.ReactNode; exact?: boolean }) => {
    const isActive = exact ? location.pathname === to : location.pathname.startsWith(to);
    return (
      <Link
        to={to}
        data-nav-to={to}
        onMouseEnter={() => setHoveredNav(to)}
        className={`nav-island-link flex items-center gap-2 whitespace-nowrap rounded-full font-medium relative z-[1] ${isActive ? 'nav-island-link--active text-white' : 'text-zinc-400 hover:text-white'}`}
      >
        <span className="flex-shrink-0 flex items-center justify-center nav-island-link-icon">{icon}</span>
        <span className="nav-island-link-label">
          {children}
          {isActive && (
            <span className="nav-island-active-dot" aria-hidden="true" />
          )}
        </span>
      </Link>
    );
  };

  return (
    // overflow-x-hidden on this root breaks position:sticky for the landing hero pin.
    // Keep overflow open on the marketing landing; clip elsewhere.
    <div className={`App relative min-h-screen bg-transparent text-foreground antialiased selection:bg-white/20 ${isLanding ? '' : 'overflow-x-hidden'}`}>
      <ScrollToTop />

      {/* 1. Global Background */}
      {!isLanding && <DottedGlowBackground />}

      {/* 2. Floating Navigation Island — liquid pill + glass spotlight */}
      {!isLanding && (
      <nav
        className={`nav-island ${isNavCondensed ? 'nav-island--condensed' : ''} ${spotlight.on ? 'nav-island--lit' : ''}`}
        style={{
          ['--spot-x' as string]: `${spotlight.x}%`,
          ['--spot-y' as string]: `${spotlight.y}%`,
        }}
        onMouseMove={onNavSpotlightMove}
        onMouseLeave={() => {
          setSpotlight((s) => ({ ...s, on: false }));
          setHoveredNav(null);
        }}
      >
        {/* FX layer is overflow-clipped so dropdowns outside the pill are not cut off */}
        <span className="nav-island-fx" aria-hidden="true">
          <span className="nav-island-spotlight" />
          <span className="nav-island-shine" />
        </span>

        <Link to="/" className="nav-island-brand flex items-center gap-2.5 mr-3 group flex-shrink-0 relative z-[1]">
          <div className="nav-island-logo rounded-lg bg-white flex items-center justify-center group-hover:scale-105 transition-transform duration-200 flex-shrink-0">
            <svg className="nav-island-logo-icon text-black" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm5-18v4h3V3h-3z" />
            </svg>
          </div>
          <span className="nav-island-brand-label font-bold tracking-tight text-white hidden md:block">
            LuminaAnalysis
          </span>
        </Link>

        {/* Desktop Links + sliding liquid indicator */}
        <div
          ref={navLinksRef}
          className="hidden md:flex items-center gap-0.5 relative"
          onMouseLeave={() => setHoveredNav(null)}
        >
          <span
            className={`nav-island-liquid ${liquid.ready ? 'nav-island-liquid--visible' : ''}`}
            style={{
              transform: `translate3d(${liquid.left}px, ${liquid.top}px, 0)`,
              width: liquid.width,
              height: liquid.height,
            }}
            aria-hidden="true"
          />
          <NavLink to="/dashboard" exact={true} icon={<Home className="w-4 h-4" />}>Dashboard</NavLink>
          <NavLink to="/patterns" icon={<CandlestickChart className="w-4 h-4" />}>Chart Pattern</NavLink>
          <NavLink to="/convergence" icon={<GitMerge className="w-4 h-4" />}>Convergence</NavLink>
          <NavLink to="/pattern-tracker" icon={<Target className="w-4 h-4" />}>Pattern Tracker</NavLink>
        </div>

        {/* Mobile Hamburger - visible only below md breakpoint */}
        <button
          className="md:hidden flex flex-col items-center justify-center w-8 h-8 gap-1.5 ml-2"
          onClick={(e) => { e.stopPropagation(); setIsMobileMenuOpen(!isMobileMenuOpen); }}
          aria-label="Toggle navigation menu"
        >
          <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
          <span className={`block w-5 h-0.5 bg-white rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>

        {/* User Actions */}
        <div className="flex items-center gap-2 pl-3 border-l border-white/10 ml-2">
          {user ? (
            <div className="relative group">
              <button
                onClick={(e) => { e.stopPropagation(); setIsProfileMenuOpen(!isProfileMenuOpen); }}
                className="nav-island-avatar rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <span className="text-sm font-bold text-white">{user.email?.[0].toUpperCase()}</span>
              </button>

              {isProfileMenuOpen && (
                <div className="absolute right-0 mt-4 min-w-[200px] rounded-2xl border border-white/10 bg-zinc-950/90 backdrop-blur-xl p-2 shadow-2xl animate-in fade-in zoom-in-95 duration-200 z-50">
                  <Link to="/profile" className="flex items-center px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    Profile
                  </Link>
                  <button onClick={logout} className="w-full flex items-center px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors mt-1">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" className="px-3 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2 hover-scale">
              Login
            </Link>
          )}
        </div>
      </nav>
      )}

      {/* Mobile Navigation Drawer */}
      {!isLanding && isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 pt-[68px]"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="mx-4 mt-2 rounded-2xl bg-zinc-950/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col p-2">
              {[
                { to: '/dashboard', label: 'Dashboard' },
                { to: '/patterns', label: 'Chart Pattern' },
                { to: '/convergence', label: 'Convergence' },
                { to: '/pattern-tracker', label: 'Pattern Tracker' },

              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`px-4 py-3.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-between ${location.pathname === item.to
                    ? 'text-white bg-white/5'
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                  {location.pathname === item.to && (
                    <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
                  )}
                </Link>
              ))}
              {user && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  <Link
                    to="/profile"
                    className="px-4 py-3.5 rounded-xl text-sm font-bold text-zinc-400 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    Profile
                  </Link>
                </div>
              )}
              {!user && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  <Link
                    to="/auth"
                    className="block px-4 py-3.5 rounded-xl text-sm font-bold text-white bg-white/10 hover:bg-white/20 transition-colors text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email verification banner — shown to logged-in users who haven't verified yet */}
      {!isLanding && user && user.emailVerified === false && (
        <EmailVerificationBanner email={user.email} />
      )}

      <main className={isLanding ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12 py-6 sm:py-8 pt-20 sm:pt-24 animate-dramatic will-change-[opacity,transform]'}>
        <Suspense fallback={
          <div className="min-h-[40vh] flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white/15 border-t-white/80 rounded-full animate-spin" />
          </div>
        }>
        <Routes>
          {/*  - Login */}
          <Route path="/" element={<Landing />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/register" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/track-record" element={<PerformancePage />} />
          <Route path="/reports" element={<QuarterlyReportsIndexPage />} />
          <Route path="/reports/quarterly" element={<Navigate to={`/reports/quarterly/${lastCompletedQuarter()}`} replace />} />
          <Route path="/reports/quarterly/:quarter" element={<QuarterlyReportPage />} />
          <Route path="/reports/pattern/:slug" element={<PatternReportPage />} />
          {/* Legacy URLs — redirect old links/bookmarks to the renamed /reports section */}
          <Route path="/quarterly-reports" element={<Navigate to="/reports" replace />} />
          <Route path="/quarterly-report" element={<Navigate to={`/reports/quarterly/${lastCompletedQuarter()}`} replace />} />
          <Route path="/quarterly-report/:quarter" element={<LegacyQuarterlyReportRedirect />} />

          {/* CoreFeature - 6 */}

          <Route path="/patterns" element={<PrivateRoute><PotentialPatternsPage /></PrivateRoute>} />

          <Route path="/pattern-tracker" element={<PrivateRoute><PatternTrackerPage /></PrivateRoute>} />
          <Route path="/convergence" element={<PrivateRoute><ConvergencePage /></PrivateRoute>} />

          {/*  */}

          <Route path="/potential-patterns" element={<PrivateRoute><PotentialPatternsPage /></PrivateRoute>} />

          {/* Deprecated: /selection, /analysis, /autoscan removed */}
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/history" element={<PrivateRoute><History /></PrivateRoute>} />

          {/* Learn pages */}
          <Route path="/learn" element={<LearnIndexPage />} />
          <Route path="/learn/smc" element={<SMCGuide />} />
          <Route path="/learn/convergence" element={<ConvergenceGuide />} />
          <Route path="/learn/patterns" element={<PatternsGuide />} />
          <Route path="/learn/mc-tvl" element={<MCTVLGuide />} />
          <Route path="/learn/ps-ratio" element={<PSRatioGuide />} />
          <Route path="/learn/patterns/head-and-shoulders" element={<HeadAndShouldersGuide />} />
          <Route path="/learn/patterns/double-top-double-bottom" element={<DoubleTopBottomGuide />} />
          <Route path="/learn/patterns/bullish-bearish-engulfing" element={<EngulfingGuide />} />
          <Route path="/learn/patterns/cup-and-handle" element={<CupAndHandleGuide />} />
          <Route path="/learn/patterns/fair-value-gap" element={<FairValueGapGuide />} />
          <Route path="/learn/patterns/order-block" element={<OrderBlockGuide />} />
          <Route path="/learn/patterns/ascending-triangle" element={<AscendingTriangleGuide />} />
          <Route path="/learn/patterns/descending-triangle" element={<DescendingTriangleGuide />} />
          <Route path="/learn/patterns/symmetrical-triangle" element={<SymmetricalTriangleGuide />} />

          {/* About pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/help" element={<HelpCenterPage />} />

          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* <Route path="/pattern-upload" element={<div>Pattern Upload</div>} /> */}
          {/* <Route path="/history" element={<div>History Stats</div>} /> */}

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </main>
      {!isLanding && <Footer />}
      <NoticeBannerStack />
    </div>
  );
}

export default App;
